/**
 * Chart Controls Component for WebGLChart
 * Toggle for show points and reset zoom button
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALE } from '../constants';

interface ChartControlsProps {
  totalPoints: number;
  onResetZoom: () => void;
  showPointsEnabled?: boolean;
  onToggleShowPoints?: () => void;
}

export const ChartControls: React.FC<ChartControlsProps> = ({
  totalPoints,
  onResetZoom,
  showPointsEnabled,
  onToggleShowPoints,
}) => {
  const { t } = useTranslation('charts');

  return (
    <div className="flex flex-col gap-2 sm:gap-3 mb-2">
      {/* Points info */}
      <div className="text-xs sm:text-sm text-muted-foreground">
        {t("dataChart.points", "Points")}:{" "}
        <span className="font-medium">
          {totalPoints.toLocaleString(LOCALE)}
        </span>
      </div>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {onToggleShowPoints !== undefined && (
          <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground cursor-pointer">
            <span className="whitespace-nowrap">{t("dataChart.showPoints", "Show points")}</span>
            <button
              type="button"
              role="switch"
              aria-checked={showPointsEnabled}
              onClick={onToggleShowPoints}
              className={`relative inline-flex h-4 w-7 sm:h-5 sm:w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                showPointsEnabled ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  showPointsEnabled ? "translate-x-3 sm:translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </label>
        )}
        <button
          className="px-2 py-0.5 sm:px-3 sm:py-1 text-xs sm:text-sm rounded-md border border-border hover:bg-muted transition-colors whitespace-nowrap"
          onClick={onResetZoom}
          aria-label={t("dataChart.aria.resetZoom", "Reset zoom")}
        >
          {t("dataChart.resetZoom", "Reset Zoom")}
        </button>
      </div>
    </div>
  );
};
